import React, {useEffect, useState} from 'react';
import Button from '@mui/material/Button';
import CssBaseline from '@material-ui/core/CssBaseline';
import TextField from '@material-ui/core/TextField';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import Link from '@material-ui/core/Link';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import {makeStyles} from '@material-ui/core/styles';
import Container from '@material-ui/core/Container';
import {withRouter} from "react-router";
import {Redirect} from "react-router-dom";
import {useFormik} from 'formik';
import {loginUser, getGoogleSignInLink, loginUserViaToken, selectOrgService} from '../../../services/auth.service';
import {useAuth} from "../../../context/auth";
import LoaderWithBackDrop from "../../../components/LoaderWithBackDrop/LoaderWithBackDrop.comp";
import OrgPickerDialog from "../../../components/OrgPickerDialog/OrgPickerDialog.comp";
import { clearAllBrandingCaches } from '../../../context/branding/branding.context';
import {LOCAL_STORAGE_KEYS, TOAST_TYPES, USER_ACCESS_LEVEL} from "../../../utils/constants";
import {toast, save_e2ee_keys} from "../../../utils/utils";
import PoweredbyDQ from "../../../assets/images/PoweredByDQ_Default.png"
import 'aos/dist/aos.css'
import AOS from 'aos'

const useStyles = makeStyles((theme) => ({
    textField: {
        '& .MuiOutlinedInput-root': {
            borderRadius: '4px',
            backgroundColor: '#ffffff',
            '&.Mui-focused': {
                backgroundColor: '#ffffff',
            },
            '&.MuiInputBase-root.Mui-disabled': {
                backgroundColor: '#f5f5f5',
            },
            '& input:-webkit-autofill': {
                boxShadow: '0 0 0 100px #ffffff inset !important',
                WebkitBoxShadow: '0 0 0 100px #ffffff inset !important',
                WebkitTextFillColor: '#000000 !important',
            },
            '& fieldset': {
                borderColor: '#ff7676',
                borderWidth: 1,
            },
            '&:hover fieldset': {
                borderWidth: 1,
                borderColor: '#ff7676',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#ff7676',
                borderWidth: 1,
            },
        },
        '& .MuiInputBase-input': {
            color: '#000000',
            padding:'15.5px 14px',
        },
        '& .MuiInputLabel-root': {
            color: '#ff7676',
        },
        '& .MuiInputLabel-root.Mui-focused': {
            color: '#ff7676',
        },
    },
    paper: {
        backgroundColor: "#F4EEEE",
        color: theme.palette.loginPage.text,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        fontFamily: 'Surt',
        [theme.breakpoints.down('sm')]: {
           height:'73vh',
        }
    },
    form: {
        width: '78%', // Fix IE 11 issue.
        marginTop: theme.spacing(1),
        color: theme.palette.background.default,
        fontFamily: 'Surt',
    },
    submit: {
        margin: theme.spacing(3, 0, 2),
        color: '#ffffff',
        width: '100%',
        fontSize: '21px',
        background: "linear-gradient(to bottom, #E7466E 35%, #FFAC46 190%) !important",
        '&:hover': {
            background: "linear-gradient(to bottom, #E7466E 35%, #FFAC46 190%) !important",
        },
    },
    section1: {

        height: '80%',
        fontFamily: 'Surt',
        '@media (min-width: 768px) and (max-width: 960px)': {
            height: '75%',
        },
        '@media (min-width: 960px) and (max-width: 1024px)': {
            height: '40%',
        },
    },

}));

export const getPostLoginRedirect = (user) => {
    // Individual learner (access 25) → default workspace; they open their
    // AI-Digital Safety report from the sidebar via Data & Insights.
    if (user?.access_level === USER_ACCESS_LEVEL.INDIVIDUAL_ACCESS_DQ_INSIDER_PORTAL) {
        return '/dq_pro/home';
    }
    if (user?.access_level === USER_ACCESS_LEVEL.PARTIAL_ACCESS_DQ_INSIDER_PORTAL_SINGLE_INSTITUTE) {
        return '/my_workspace';
    }
    // An LC Teacher lands on their workspace home, as /login sends them — also
    // after the first-login password change, which returns here (LA-TW-19Oct-01).
    if (user?.access_level === USER_ACCESS_LEVEL.TEACHER_ACCESS_DQ_INSIDER_PORTAL) {
        return '/my_workspace';
    }
    return '/dq_pro/program/creation/preview';
};

const Login = (props) => {
    // const [isLoggedIn, setLoggedIn] = useState(false);
    const classes = useStyles();

    const {setAuthToken, setAuthUser} = useAuth();
    const referer = props.location.referer || '/dq_pro/home';
    const [loading, setLoading] = useState(false);

    // Multi-org picker state
    const [orgPickerOpen, setOrgPickerOpen] = useState(false);
    const [orgPickerOrgs, setOrgPickerOrgs] = useState([]);
    const [preAuthToken, setPreAuthToken] = useState(null);
    const [orgSelecting, setOrgSelecting] = useState(false);
    const [orgTokenExpired, setOrgTokenExpired] = useState(false);

    /** Shared helper — persists auth data and navigates after a successful login response */
    const handleAuthSuccess = async (data) => {
        clearAllBrandingCaches();
        await setAuthToken(data.auth_token);
        await setAuthUser(data.user);
        localStorage.setItem(LOCAL_STORAGE_KEYS.refresh_token, JSON.stringify(data.refresh_token));
        localStorage.setItem(LOCAL_STORAGE_KEYS.user_email, JSON.stringify(data.user.email));
        if (data.e2ee_pub_key) save_e2ee_keys({ data });
        props.history.push(getPostLoginRedirect(data.user));
    };

    const formik = useFormik({
        initialValues: {
            email: '',
            password: '',
            remember_me: false
        },
        onSubmit: async (values) => {
            console.log('Form data => ', values);
            try {
                setLoading(true);
                const result = await loginUser(values.email, values.password, values.remember_me);
                console.log('result.data -> ', result.data);

                // Multi-org branch: show workspace picker
                if (result.requires_org_selection) {
                    setPreAuthToken(result.data.pre_auth_token);
                    setOrgPickerOrgs(result.data.orgs || []);
                    setOrgTokenExpired(false);
                    setOrgPickerOpen(true);
                    setLoading(false);
                    return;
                }

                if (result.data && result.data.user) {
                    await handleAuthSuccess(result.data);
                }
                setLoading(false);
            } catch (e) {
                // already toasted the error
                setLoading(false);
            }
        }
    });

    const loginViaToken = async (token) => {
        try {
            setLoading(true);
            const result = await loginUserViaToken(token, false);

            // v1.2 — Google OTP callback can also trigger org picker
            if (result.requires_org_selection) {
                setPreAuthToken(result.data.pre_auth_token);
                setOrgPickerOrgs(result.data.orgs || []);
                setOrgTokenExpired(false);
                setOrgPickerOpen(true);
                return;
            }

            if (result.data && result.data.user) {
                localStorage.removeItem(LOCAL_STORAGE_KEYS.dqpro_v2_google_auth_redirect);
                await handleAuthSuccess(result.data);
            } else {
                setLoading(false);
            }
        } catch (e) {
            setLoading(false);
        } finally {
            // setLoading(false) not needed here for the happy path — page navigates away
        }
    };

    /** User selected a workspace in the org picker */
    const handleOrgSelect = async (dq_insider_id, role) => {
        try {
            setOrgSelecting(true);
            const result = await selectOrgService(preAuthToken, dq_insider_id, role);
            if (result.data && result.data.user) {
                setOrgPickerOpen(false);
                await handleAuthSuccess(result.data);
            }
        } catch (e) {
            // pre_auth_token expired or invalid
            setOrgTokenExpired(true);
        } finally {
            setOrgSelecting(false);
        }
    };

    /** User dismissed the org picker — reset so they can re-enter credentials */
    const handleOrgPickerCancel = () => {
        setOrgPickerOpen(false);
        setPreAuthToken(null);
        setOrgPickerOrgs([]);
        setOrgTokenExpired(false);
    };

    const loginWithGoogleAction = async () => {
        setLoading(true);
        try {
            const result = await getGoogleSignInLink();
            const { redirect_link } = result.data;
            window.open(redirect_link, '_self');
            // Do NOT setLoading(false) — page navigates away
        } catch (e) {
            setLoading(false);
        }
    };

    useEffect(() => {
        AOS.init({
            duration: 500,
        });
    }, []);

    // Handle Google OAuth2 redirect callback on mount
    useEffect(() => {
        const params = new URLSearchParams(props.location.search);
        const access_token = params.get('otp_token');
        const error = params.get('error');
        if (error) {
            toast(TOAST_TYPES.DANGER, 'Google Login Failed', error);
        } else if (access_token) {
            loginViaToken(access_token).then(r => console.log('Logged in via Google callback token'));
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <>
           <Grid container className={classes.section1} data-aos="fade-in" data-aos-delay='100' style={{backgroundColor:"#F4EEEE", height:'100vh'}}>
                    <Container component="main" maxWidth="sm" style={{
                            marginTop:'10%',}}>
                       <div className={classes.paper} style={{
                                paddingBottom: '50px',
                                backgroundColor:'#F4EEEE',
                       }}>
                                <img src={PoweredbyDQ} alt="Image"
                                     style={{
                                         width: '250px',
                                         cursor: 'pointer'
                                     }}/>
                                 {process.env.REACT_APP_ENABLE_LOGIN_WITH_GOOGLE === '1' && (
                                     <div style={{ width: '78%', marginTop: '16px' }}>
                                         <Button
                                             fullWidth
                                             variant="outlined"
                                             onClick={loginWithGoogleAction}
                                             disabled={loading}
                                             style={{
                                                 backgroundColor: '#ffffff',
                                                 color: '#1a1a1a',
                                                 border: '1.5px solid #dedede',
                                                 borderRadius: '8px',
                                                 fontSize: '15px',
                                                 fontWeight: 600,
                                                 textTransform: 'none',
                                                 padding: '10px 16px',
                                                 display: 'flex',
                                                 alignItems: 'center',
                                                 justifyContent: 'center',
                                                 gap: '10px',
                                             }}
                                         >
                                             <img
                                                 src="https://www.gstatic.com/images/branding/product/1x/gsa_512dp.png"
                                                 alt="Google"
                                                 style={{ width: '20px', height: '20px' }}
                                             />
                                             Login with Google
                                         </Button>
                                         <div style={{ display: 'flex', alignItems: 'center', margin: '16px 0' }}>
                                             <div style={{ flex: 1, height: '1px', backgroundColor: '#ccc' }} />
                                             <span style={{ margin: '0 12px', color: '#999', fontSize: '14px', fontWeight: 700 }}>OR</span>
                                             <div style={{ flex: 1, height: '1px', backgroundColor: '#ccc' }} />
                                         </div>
                                     </div>
                                 )}
                                <form className={classes.form} noValidate onSubmit={formik.handleSubmit}
                                      style={{fontFamily: 'Surt'}}>
                                    <TextField
                                        className={classes.textField}
                                        variant="outlined"
                                        margin="normal"
                                        required
                                        fullWidth
                                        id="email"
                                        label="Email Address"
                                        name="email"
                                        autoComplete="email"
                                        autoFocus
                                        onChange={formik.handleChange}
                                        value={formik.values.email}
                                    />
                                    <TextField
                                        className={classes.textField}
                                        style={{borderRadius: '30px'}}
                                        variant="outlined"
                                        margin="normal"
                                        required
                                        fullWidth
                                        name="password"
                                        label="Password"
                                        type="password"
                                        id="password"
                                        autoComplete="current-password"
                                        onChange={formik.handleChange}
                                        value={formik.values.password}
                                    />
                                    <Grid container>
                                        <FormControlLabel
                                            control={<Checkbox
                                                name="remember_me"
                                                id="remember_me"
                                                value={formik.values.remember_me}
                                                onChange={formik.handleChange}
                                            />}
                                            label="Remember me"
                                            style={{color: '#ff7676', fontFamily: 'Surt'}}
                                        />
                                        <Grid item xs style={{
                                            textAlign: 'right',
                                            paddingTop: '2%',
                                            fontFamily: 'Surt',
                                            color: '#ff7676'
                                        }}>
                                            <Link href="/account/recover" variant="body2">
                                                Forgot password?
                                            </Link>
                                        </Grid>
                                    </Grid>
                                    <Typography align='center'>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            sx={{
                                                backgroundColor: '#ff6468',
                                                width: '50%',
                                                mt:3,
                                                fontWeight: 'bold',
                                                fontSize: '16px',
                                                borderRadius: '20px',
                                                '&:hover': {
                                                    backgroundColor: '#ff6468',
                                                    color:'#ffffff'
                                                }
                                            }}
                                        >
                                            Sign In
                                        </Button>
                                    </Typography>

                                    <Grid item style={{textAlign: 'center',marginTop:'50px', fontFamily: 'Surt',color: '#000000',}}>
                                        <Typography align='center' style={{fontWeight:'bold',fontSize:'19px'}}>
                                            Don’t have an account?
                                        </Typography>
                                            <Typography align='center' style={{fontSize:'19px'}}>
                                                Sign up to join Powered by DQ {' '}
                                            <Link target='_blank' href="/signup" variant="body2" style={{textDecoration:'underline',fontWeight:'bold',color: '#ff7676',fontSize:'21px'}}>
                                            {"here"}
                                        </Link>
                                        </Typography>
                                    </Grid>

                                </form>
                            </div>

                            <LoaderWithBackDrop loading={loading}/>
                        </Container>
                    </Grid>
                {/*</Grid>*/}

            <OrgPickerDialog
                open={orgPickerOpen}
                orgs={orgPickerOrgs}
                loading={orgSelecting}
                expired={orgTokenExpired}
                onSelect={handleOrgSelect}
                onCancel={handleOrgPickerCancel}
            />
        </>
    );
}

export default withRouter(Login);

